import { UserInvoiceDetailSerializer } from "@app-contexts/user/features/my-orders/data/types/invoice";
import { enums, INVOICE_ITEM_TYPE_OPTIONS } from "@shared/constants";
import { PurchaseSourcePlatformMapIdType } from "@shared/constants/enums/enums-types";
import { PublicInternalAnalyticsTrackingSerializer } from "@shared/modules/analytics/types";
import { TicketBasketType } from "@shared/modules/basket/types";
import { DataUtils } from "./DataUtils";
import { IInteractionDataBuilder } from "./IInteractionDataBuilder";

/**
 * Builder for purchase cart events.
 * Includes helper methods to extract event and venue ids from a basket.
 */
export class PurchaseCartDataBuilder implements IInteractionDataBuilder {
  private basket: TicketBasketType;

  private invoice?: UserInvoiceDetailSerializer;

  private purchase_source_platform?: PurchaseSourcePlatformMapIdType;

  constructor(basket: TicketBasketType, invoice?: UserInvoiceDetailSerializer) {
    this.basket = basket;
    this.invoice = invoice;
    this.purchase_source_platform = basket.purchase_source_platform;
  }

  private static getVenueIdFromBasket(
    basket: TicketBasketType
  ): number | undefined {
    if (!basket.item_groups) {
      return undefined;
    }
    const venueItemGroups = basket.item_groups.filter(
      (ig) =>
        ig.item_type !== INVOICE_ITEM_TYPE_OPTIONS.INVOICE_ITEM_TYPE_DONATION.id
    );
    if (venueItemGroups.length > 0) {
      return venueItemGroups[0].venue_id;
    }
    return undefined;
  }

  build(): PublicInternalAnalyticsTrackingSerializer {
    if (!this.basket.item_groups) {
      throw new Error("Invalid basket – item_groups missing");
    }

    let customParamsObj: {
      showpass_fees: string | undefined;
      cc_fees: string | undefined;
      item_quantity: number;
      invoice_id?: number;
      purchase_source_platform?: PurchaseSourcePlatformMapIdType;
      refund_protection_purchased?: boolean;
      refund_protection_amount?: string;
    } = {
      showpass_fees: this.basket.company_fees,
      cc_fees: this.basket.cc_fees,
      item_quantity: this.basket.item_groups.reduce(
        (quantity, { quantity_included_in_total }) =>
          quantity + (quantity_included_in_total ?? 0),
        0
      ),
    };

    if (this.invoice) {
      customParamsObj = { ...customParamsObj, invoice_id: this.invoice.id };
    }
    if (this.purchase_source_platform) {
      customParamsObj = {
        ...customParamsObj,
        purchase_source_platform: this.purchase_source_platform,
        refund_protection_purchased: Boolean(
          this.basket.refund_protection_opt_in
        ),
        refund_protection_amount: this.basket.refund_protection_opt_in
          ? this.basket.protection_fees
          : undefined,
      };
    }

    return {
      interaction_type: enums.INTERACTION_TYPE_MAP.PURCHASE.id,
      label: enums.INTERACTION_LABEL_MAP.PURCHASE.id,
      venue_id: PurchaseCartDataBuilder.getVenueIdFromBasket(this.basket),
      basket_id: this.basket.id,
      custom_params: customParamsObj,
      amount: this.basket.total_price,
      event_ids_for_conversions: DataUtils.getEventIdsFromBasket(this.basket),
      product_ids_for_conversions: DataUtils.getProductIdsFromBasket(
        this.basket
      ),
      membership_ids_for_conversions: DataUtils.getMembershipIdsFromBasket(
        this.basket
      ),
    };
  }
}
